import { useFormik } from 'formik';
import useTranslate from 'hooks/useTranslate';
import { ReactNode, useMemo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import ReactSelect from 'react-select';
import * as Yup from 'yup';
import ActionMenu from '../ActionMenu';
import AddMoreButton from '../AddMoreButton';
import InfoCard from '../InfoCard';
import useResumeStore from 'store/resume';
import { useGetCommonDataQuery } from 'services';

interface SocialMediaInterface {
	title: string;
	socialLink: string;
}

const SocialMedia = ({
	data,
	slug,
	handleRemoveClick,
	onHandleChange,
	handleModalVisibility,
	handleTitleChange,
	checkLimitExceed,
}: any) => {
	// hooks
	const { translate } = useTranslate();

	// mutation
	const { data: commonData = {}, refetch } = useGetCommonDataQuery();

	const { socialIcons } = commonData ?? {};

	const socialMediaArr = useMemo(() => {
		const arr = [{ id: '', title: 'Select Social Handle' }];
		if (socialIcons?.length) {
			arr.push(...socialIcons);
		}
		return arr;
	}, [socialIcons]);

	// store
	const fieldVisibility = useResumeStore(
		(state: any) => state?.resumeData?.manageTemplateFields?.[slug] || false,
	);
	const storedSocialData = useResumeStore((state: any) => state.resumeData?.social ?? []);
	const dataLimit = useResumeStore((state: any) => state.resumeData?.limits?.[slug] ?? 1);
	const validationSchema = Yup.object().shape({
		title: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.handle') }),
		),
		socialLink: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.socialLink') }),
		),
	});

	const formik = useFormik({
		initialValues: {
			id: '',
			title: '',
			socialLink: '',
			icon: '',
		},
		validationSchema: validationSchema,
		onSubmit: (values: any) => {
			const objSocial = {
				title: values?.title,
				socialLink: values?.socialLink,
				icon: values?.icon,
			};
			onHandleChange(slug, values?.id, objSocial);
			formik.resetForm();
		},
	});

	const renderError = <T extends keyof SocialMediaInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const checkLimits = useMemo(() => {
		return checkLimitExceed(slug);
	}, [data?.length, fieldVisibility]);

	return (
		<div className="resume-info">
			<Form onSubmit={formik.handleSubmit} autoComplete="off">
				<div className="build-resume-edit-header">
					<ActionMenu
						data={data}
						handleRemoveClick={handleRemoveClick}
						slug="socialTitle"
						contentSlug={slug}
						handleModalVisibility={handleModalVisibility}
						formikObj={formik}
						handleTitleChange={handleTitleChange}
					/>
					<p className="mb-0">{translate('Resume.socialSubtitle', { field: dataLimit })}</p>
				</div>
				{fieldVisibility && (
					<div className="build-resume-edit-fields">
						<Row>
							{checkLimits && (
								<>
									<Col xl="6">
										<div className="text-start">
											<Form.Group className="mb-4 position-relative">
												<Form.Label className="lh-1">
													{translate('PlaceHolder.socialHandle')}
												</Form.Label>
												<ReactSelect
													className="basic-single"
													classNamePrefix="select"
													isSearchable
													name="title"
													menuPlacement="top"
													options={socialMediaArr}
													getOptionValue={(option: any) => option.id}
													getOptionLabel={(option: any) => option.title}
													onChange={(value: any) => {
														formik.setFieldValue('id', value?.id);
														formik.setFieldValue('icon', value?.image);
														formik.setFieldValue('title', value?.title);
													}}
													isOptionDisabled={(option: any) =>
														storedSocialData.some((item: any) => item?.id === option?.id)
													}
													autoFocus={false}
													placeholder={translate('PlaceHolder.selectSocial')}
													value={socialMediaArr?.find(
														(value: any) => value.id === formik.values.title,
													)}
												/>
												{renderError('title')}
											</Form.Group>
										</div>
									</Col>
									<Col xl="6">
										<div className="text-start">
											<Form.Group className="mb-4 position-relative">
												<Form.Label className="lh-1">
													{translate('PlaceHolder.enterLink')}
												</Form.Label>
												<Form.Control
													id="socialLink"
													name="socialLink"
													onChange={(event: any) => {
														formik.setFieldValue(event.target.name, event.target.value);
													}}
													onBlur={formik.handleBlur}
													value={formik.values.socialLink}
													type="text"
													placeholder={translate('PlaceHolder.enterLink')}
												/>
												{renderError('socialLink')}
											</Form.Group>
										</div>
									</Col>
									<Col>
										<AddMoreButton title={translate('Resume.addSocial')} />
									</Col>
								</>
							)}
						</Row>
						{data?.map((field: any, index: number) => {
							const { title, image, socialLink } = field ?? {};
							return (
								<InfoCard
									key={index}
									content={{
										title,
										description: socialLink,
										logo: image,
									}}
									slug={slug}
									index={index}
									handleRemove={handleRemoveClick}
									handleModalVisibility={handleModalVisibility}
								/>
							);
						})}
					</div>
				)}
			</Form>
		</div>
	);
};

export default SocialMedia;
